import React from "react";
import Form from "react-bootstrap/Form";

interface FieldProps {
  // Layout
  label?: React.ReactNode;
  hint?: string;
  error?: string;
  required?: boolean;
  margin?: string;

  // Leading icon: Bootstrap Icon class (e.g. "bi-envelope") or a custom
  // ReactNode (lets callers preserve a bespoke SVG when migrating).
  icon?: string;
  iconNode?: React.ReactNode;

  // Right-side slot — e.g. a password eye toggle, or a clear button.
  rightSlot?: React.ReactNode;

  // Optional character counter for text-length-bound fields.
  maxLength?: number;
  currentLength?: number;
  showCounter?: boolean;

  // Visual variant — "card" matches the dashboard's themed surfaces.
  // "plain" is for cases where the input sits inside a custom shell.
  variant?: "card" | "plain";

  // Forwarded to the outer Form.Group so callers (Text's `parentStyles`)
  // can position the field without an extra wrapping div.
  style?: React.CSSProperties;

  // The actual input element goes here (Form.Control / PatternFormat /
  // DatePicker / NumericFormat / etc.).
  children: React.ReactNode;
}

// Shared field shell used by every control in `controls/*`. Owns the
// label + icon + input wrapper + hint/error/counter row, so individual
// controls only need to declare their own input element.
const Field: React.FC<FieldProps> = ({
  label,
  hint,
  error,
  required,
  margin,
  icon,
  iconNode,
  rightSlot,
  maxLength,
  currentLength,
  showCounter,
  variant = "card",
  style,
  children,
}) => {
  const hasIcon = Boolean(icon || iconNode);
  const wrapClass = [
    "field__inputWrap",
    `field__inputWrap--${variant}`,
    hasIcon ? "field__inputWrap--hasIcon" : "",
    rightSlot ? "field__inputWrap--hasRight" : "",
    error ? "field__inputWrap--error" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <Form.Group
      className={`field ${margin === "mb-0" ? "mb-0" : "mb-3"}`}
      style={style}
    >
      {label && (
        <Form.Label className="field__label">
          {label}
          {required && (
            <span className="field__required" aria-hidden="true">
              *
            </span>
          )}
        </Form.Label>
      )}
      <div className={wrapClass}>
        {iconNode ? (
          <span className="field__icon field__icon--node">{iconNode}</span>
        ) : icon ? (
          <i className={`bi ${icon} field__icon`} aria-hidden="true" />
        ) : null}
        {children}
        {rightSlot && <div className="field__rightSlot">{rightSlot}</div>}
      </div>
      {(hint || error || showCounter) && (
        <div className="field__meta">
          {(hint || error) && (
            <span
              className={`field__hint ${error ? "field__hint--error" : ""}`}
            >
              {error || hint}
            </span>
          )}
          {showCounter && maxLength && (
            <span className="field__counter">
              {currentLength ?? 0}/{maxLength}
            </span>
          )}
        </div>
      )}
    </Form.Group>
  );
};

export default Field;
